import React, { useEffect, useState } from "react";
import { Button, Col, FloatingLabel, Form, Modal, Row, Spinner } from "react-bootstrap";
import PrivateApi from "../../services/PrivateApiService";
import { FaCheckCircle } from "react-icons/fa";
import EmployeeStage from "../anlage/new_anlage/EmployeeStage";
import { useAuthState } from "../../context/AuthContext";
import { useQueryClient } from "react-query";
import { FaArrowRight, FaFileInvoiceDollar } from "react-icons/fa6";
import { MdOutlineSend } from "react-icons/md";
import energoLogo from "../../color-logo.svg";
import Validator from "../../services/Validator";


/**
 * 
 * @param {props} show boolean to show/hide modal
 * @param {props} setShow boolean setter to set show
 * @param {props} elv object elv
 * 
 * @returns html modal
 */
const MissingInvoiceModal = ({show, setShow, elv}) => {

    const API = new PrivateApi();
    const queryClient = useQueryClient();
    const {user, contactPerson} = useAuthState();
    const [stage, setStage] = useState("default");
    const [monthSelect, setMonthSelect] = useState("");
    const [yearSelect, setYearSelect] = useState("");
    const [invoiceSum, setInvoiceSum] = useState("");
    const [description, setDescription] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});

    const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
    const years = [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() -2];


    useEffect(() => {
        if (show === true) {
            if (user.x_is_house_management === true) {
                if (contactPerson === undefined || contactPerson === null) {
                    return setStage("contact") ;
                }
            }
            setStage("default");
        }
    }, [show])


    // handler for closing
    const closeHandler = () => {
        setStage("default");
        setFieldErrors({});
        setMonthSelect("");
        setYearSelect("");
        setDescription("");
        setInvoiceSum("");
        setShow(false);
    }


    // handler to send data to backend
    const sendDataHandler = () => {
        var temp = {};
        if (monthSelect === "") temp.monthSelect = true;
        if (yearSelect === "") temp.yearSelect = true;
        if (!isInvoiceSumValid(invoiceSum) && invoiceSum !== "") temp.invoiceSum = true;

        if (Object.keys(temp).length > 0) {
            return setFieldErrors(temp);
        }

        setStage('loading');
        var data = {elv: elv, month: monthSelect, year: yearSelect, comment: description};

        if (invoiceSum !== "" && isInvoiceSumValid(invoiceSum)) data.sum = invoiceSum;

        if (user.x_is_house_management && contactPerson !== null && contactPerson !== undefined) {
            data['contact'] = contactPerson;
        }

        API.post_Abrechnung_missing(data)
            .then(() => {
                queryClient.invalidateQueries("GET_ELV_DETAILS_QUERY");
                setStage("success");
            })
            .catch(() => {
                setStage("error");
            })
    }

    const invoiceSumChangeHandler = (e) => {
        const value = e.target.value;
        if ([...value].every(char => "0123456789,.".includes(char))) {
            setInvoiceSum(value);
        }
    }

    const isInvoiceSumValid = (value) => {
        if (typeof value !== "string" || value.trim() === "") {
            return false;
        }
        return /^(?:\d{1,3}(?:\.\d{3})+|\d+)(?:,\d{1,2})?$/.test(value.trim());
    };

    return (
        <Modal show={show} onHide={() => closeHandler()}>
            <Modal.Header className="bg-violet text-light d-flex gap-2">
                <FaFileInvoiceDollar size={30} />
                <b>Fehlende Abrechnung</b>
            </Modal.Header>
            <Modal.Body>
                {
                    stage === 'contact'?
                        <EmployeeStage setShow={setShow} setStage={setStage} /> :

                    stage === 'default'?
                        <div className="mt-2">
                            <p>
                                <small className="text-muted">
                                    Ich kann eine Abrechnung zu diesem Energieliefervertrag 
                                    nicht finden. Bitte leiten Sie eine Recherche ein.
                                </small>
                            </p>
                            <br />
                            <Row className="mb-3">
                                <Col xs="6">
                                    <Form.Select
                                        value={monthSelect}
                                        onChange={e => {setMonthSelect(e.target.value); setFieldErrors({})}}
                                        isValid={monthSelect !== ""}
                                        isInvalid={fieldErrors.monthSelect}
                                    >
                                        <option value="">Monat</option>
                                        {months.map(m => (
                                            <option key={m} value={m}>{m}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col xs="6">
                                    <Form.Select
                                        value={yearSelect}
                                        onChange={e => {setYearSelect(e.target.value); setFieldErrors({})}}
                                        isValid={yearSelect !== ""}
                                        isInvalid={fieldErrors.yearSelect}
                                    >
                                        <option value="">Jahr</option>
                                        {years.map(y => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col>
                                    <FloatingLabel label="Rechnungsbetrag (falls verfügbar)">
                                        <Form.Control
                                            value={invoiceSum}
                                            onChange={invoiceSumChangeHandler}
                                            placeholder="Rechnungsbetrag (falls verfügbar)"
                                            isValid={isInvoiceSumValid(invoiceSum)}
                                            isInvalid={fieldErrors.invoiceSum}
                                        />
                                    </FloatingLabel>
                                </Col>
                            </Row>
                            <FloatingLabel label="Nachricht">
                                <Form.Control 
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    isValid={description !== ""}
                                    as="textarea" 
                                    placeholder="Nachricht" 
                                    style={{height: "100px"}}
                                />
                            </FloatingLabel>
                        </div>:
                    stage === 'loading'?
                        <div className="text-center">
                            <p>Daten werde am <span className="text-energo">ENERGO</span> übermittelt...</p>
                            <Spinner animation="border" className="text-danger"/>
                        </div>:
                    stage === 'success'?
                        <div className="text-center">
                            <div className="d-flex gap-4 align-items-center justify-content-center">
                                <FaFileInvoiceDollar size={60} className="text-danger" />
                                <FaArrowRight size={30} />
                                <MdOutlineSend size={40} />
                                <FaArrowRight size={30} />
                                <div className="d-flex flex-column align-items-center">
                                    <img src={energoLogo} alt="energo" width={70} />
                                    <FaCheckCircle size={20} className="text-energo" />
                                </div>
                            </div>
                            <p className="mt-3">
                                Wir haben Ihre Anfrage bekommen und melden uns, sobald wir die Abrechnung erhalten haben.
                            </p>
                        </div>:
                    stage === 'error'?
                        <div>
                            <p className="text-danger">
                                Fehler aufgetreten, bitte versuchen Sie später nochmal.
                            </p>
                        </div>
                :null}
            </Modal.Body>
            <Modal.Footer>
                {
                    stage === 'default' ? 
                        <div>
                            <Button onClick={() => closeHandler()} variant="warning">Abbrechen</Button>
                            &nbsp;
                            <Button onClick={() => sendDataHandler()} className="btn-energo">Absenden</Button>
                        </div>:
                    stage === 'success' ?
                        <div>
                            <Button onClick={() => closeHandler()} className="btn-energo">Schließen</Button>
                        </div>:
                    stage === 'error' ?
                        <Button onClick={() => closeHandler()} className="btn-energo">Schließen</Button>
                    :null
                }
            </Modal.Footer>
        </Modal>
    )
}

export default MissingInvoiceModal;